import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { branding, documentMimeTypes } from '../../../config/enum';
import { NewProgressProps as NewProgressProperties } from '../../../interface/file-upload-stream.interface';
import { useAppDispatch } from '../../../redux/hooks';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../../redux/slices/FileUploadStreamSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
// import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import {
  checkFileSizeValidation,
  emailValidation,
  passwordValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import auth from '../../../utils/api/v2/auth';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}
interface Credentials {
  name: string;
  password: string;
  confirmPassword: string;
  idProof: File[];
  email: string;
  phoneNumber: string;
  countryCode: string;
  [key: string]: string | number | File[];
  accountName: string;
}

/**
 * Register component
 * @returns {any} The rendered Register component.
 * @description Register component is a component for register functionality.
 */
function RegisterV3(): React.JSX.Element {
  const { t, i18n } = useTranslation('loginV2');
  const [fileInputKey, setFileInputKey] = useState(0);
  // const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  const [credentials, setCredentials] = useState<Credentials>({
    name: '',
    password: '',
    confirmPassword: '',
    email: '',
    phoneNumber: '',
    idProof: [],
    countryCode: '966',
    accountName: '',
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });

  /**
   * Function handle register data change
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number | File[]) => {
    setCredentials({
      ...credentials,
      [name]: changeValue,
    });
    // If the idProof is being cleared (file removed), reset the key
    if (name === 'idProof' && Array.isArray(changeValue) && changeValue.length === 0) {
      setFileInputKey((previous) => previous + 1);
    }
  };

  /**
   * Function that navigate to login page
   */
  const handleNavigate = () => {
    setModalBoxStatus('close');
    navigate('../login/credentials');
  };

  /**
   * Whether the new-password field should show an error state.
   * @returns {boolean} true when field is empty or fails the password rule
   */
  const hasPasswordError = () =>
    formStatus.showFormErrors && !passwordValidation(credentials.password);

  /**
   * Resolves which message to show under the new-password field: required
   * first, otherwise the format rule.
   * @returns {string} translated error message
   */
  const passwordErrorMessage = () =>
    credentials.password ? t('passwordMustBeAtLeast12Char') : t('pleaseEnterPassword');

  /**
   * Whether the confirm-password field should show an error state.
   * @returns {boolean} true when field is empty or doesn't match the password
   */
  const hasConfirmPasswordError = () =>
    formStatus.showFormErrors && credentials.confirmPassword !== credentials.password;

  /**
   * Resolves which message to show under the confirm-password field:
   * required first, otherwise the mismatch message.
   * @returns {string} translated error message
   */
  const confirmPasswordErrorMessage = () =>
    credentials.confirmPassword ? t('passwordMismatch') : t('confirmPasswordRequired');

  /**
   * Function to get and handle file upload progress
   * @param {any} progress - The progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };
  /**
   * Register API call
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    const emailProvided = !!credentials.email;
    const phoneProvided = !!credentials.phoneNumber;
    const emailValid = emailProvided ? emailValidation(credentials.email) : true;
    const phoneValid = phoneProvided ? phoneNumberValidation(credentials.phoneNumber) : true;
    const atLeastOne = emailProvided || phoneProvided;

    if (
      credentials.countryCode !== '' &&
      stringValidation(1, 100, credentials.name) &&
      stringValidation(1, 100, credentials.accountName) &&
      atLeastOne &&
      emailValid &&
      phoneValid &&
      passwordValidation(credentials.password) &&
      (process.env.REACT_APP_BRANDING === branding.VOLTIE ||
        checkFileSizeValidation(credentials.idProof, 5, 1, 0, documentMimeTypes)) &&
      credentials.password === credentials.confirmPassword
    ) {
      setFormStatus({
        showFormErrors: true,
        hint: false,
        buttonLoader: true,
      });

      const formData = new FormData();
      formData.append('accountName', credentials.accountName);
      formData.append('name', credentials.name);
      formData.append('password', credentials.password);
      if (credentials.email) {
        formData.append('email', credentials.email.toLowerCase());
      }
      if (credentials.phoneNumber) {
        formData.append('countryCode', credentials.countryCode);
        formData.append('phoneNumber', credentials.phoneNumber);
      }

      if (credentials.idProof.length > 0) {
        formData.append('governmentId', credentials.idProof[0]);
      }

      dispatch(
        setUnUploadedFileData([
          {
            // fileName: credentials.idProof[0].name,
            fileName: 'QR_Codes_pdf',
            progress: 0,
            status: 'pending',
          },
        ]),
      );

      auth
        .selfRegistration(formData, handleUploadProgress)
        .then(() => {
          setModalBoxStatus('open');
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('accountRegisteredSuccessfully')}
                description={t('yourAccountHasBeenRegistered')}
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigate}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          dispatch(
            setNotificationMessage({
              type: 'error',
              status: true,
              message: error?.response
                ? error?.response?.data?.message || t('somethingWentWrong')
                : t('noInternetConnection'),
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  return (
    <div className="register-v2-wrapper" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="register-form-wrap">
        <h1 className="title">{t('signUp')}</h1>
        <div className="register-form-container">
          <div className="register-form">
            <CustomInput
              inputLabel={t('accountName')}
              type="text"
              placeholder={t('enterAccountName')}
              valueRequired
              name="accountName"
              value={credentials.accountName}
              onChangeValue={handleCredentialChange}
              error={
                !stringValidation(1, 100, credentials.accountName) && formStatus.showFormErrors
              }
              errorMessage={t('accountNameRequired')}
            />
            <CustomInput
              inputLabel={t('userName')}
              type="email"
              placeholder={t('enterUserName')}
              valueRequired
              name="name"
              value={credentials.name}
              onChangeValue={handleCredentialChange}
              error={!stringValidation(1, 100, credentials.name) && formStatus.showFormErrors}
              errorMessage={t('nameIsRequired')}
              hint
              hintMessage={t('pleaseEnsure')}
            />
            <div className="form-group">
              <CustomInput
                inputLabel={t('email')}
                type="email"
                placeholder={t('enterEmail')}
                name="email"
                value={credentials.email}
                onChangeValue={handleCredentialChange}
                error={
                  credentials.email !== '' &&
                  !emailValidation(credentials.email) &&
                  formStatus.showFormErrors
                }
                errorMessage={t('invalidEmailId')}
              />
              <CustomPhoneNumber
                inputLabel={t('phoneNumber')}
                placeholder={t('enterPhoneNumber')}
                name="phoneNumber"
                value={credentials.phoneNumber}
                onChangeValue={handleCredentialChange}
                error={
                  credentials.phoneNumber !== '' &&
                  !phoneNumberValidation(credentials.phoneNumber) &&
                  formStatus.showFormErrors
                }
                errorMessage={t('invalidPhoneNumber')}
                countryCode={credentials.countryCode}
              />
            </div>
            <CustomFileInput
              key={fileInputKey}
              inputLabel={t('governmentId')}
              acceptedFileType="application/pdf"
              // acceptedFileType="image/*, video/*, application/pdf"
              placeholder={t('uploadAFile')}
              valueRequired={process.env.REACT_APP_BRANDING !== branding.VOLTIE}
              name="idProof"
              selectedFiles={credentials.idProof}
              onChangeValue={handleCredentialChange}
              error={
                process.env.REACT_APP_BRANDING !== branding.VOLTIE &&
                !(
                  credentials.idProof.length > 0 &&
                  checkFileSizeValidation(credentials.idProof, 5, 1, 0, documentMimeTypes)
                ) &&
                formStatus.showFormErrors
              }
              errorMessage={t('governmentIdProofIsRequired')}
            />
            <div className="form-group">
              <CustomInput
                inputLabel={t('createPassword')}
                type="password"
                placeholder={t('enterPassword')}
                valueRequired
                name="password"
                value={credentials.password}
                onChangeValue={handleCredentialChange}
                error={hasPasswordError()}
                errorMessage={passwordErrorMessage()}
              />
              <CustomInput
                inputLabel={t('confirmPassword')}
                type="password"
                placeholder={t('reEnterPassword')}
                valueRequired
                name="confirmPassword"
                value={credentials.confirmPassword}
                onChangeValue={handleCredentialChange}
                error={hasConfirmPasswordError()}
                errorMessage={confirmPasswordErrorMessage()}
              />
            </div>
            {!passwordValidation(credentials.password) && (
              <p className="hint-message">{t('passwordMustBeAtLeast12Char')}</p>
            )}
          </div>
          <div className="register-button-wrap">
            <MyButton
              label={t('signUp')}
              disableHoverEffect
              buttonType="submit"
              onClickFunc={handleSubmit}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
            <div className="login-account">
              <span>{t('doYouHaveAnAccountAlready')}</span>
              <span role="presentation" onClick={() => navigate('../login/credentials')}>
                {t('signIn')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterV3;
